import { useQuery } from "@tanstack/react-query";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import api from "@/common/utils/api";
import { featureQueryKeys } from "@/feature/data/featureQueryKeys";
import { FeatureDetail, LinearCleanupIssue } from "@reflag/shared/featureAPI";

export function useLatestLinearCleanupIssue(
  featureId?: string | null,
  feature?: Pick<
    FeatureDetail,
    "linearCleanupIssueUrl" | "linearCleanupIssueDetails"
  >,
): Pick<LinearCleanupIssue, "url" | "details"> | null {
  const { appId, envId, org } = useEnvironment();
  const { data } = useQuery({
    queryKey: featureQueryKeys.linearCleanupIssues(
      appId,
      envId,
      featureId ?? undefined,
    ),
    queryFn: () =>
      api
        .get<"/apps/:appId/features/:featureId/linear-cleanup-issues">(
          `/apps/${appId}/features/${featureId}/linear-cleanup-issues`,
        )
        .then((res) => res.data),
    select: (collection) => collection.data[0] ?? null,
    enabled: !!appId && !!featureId,
    staleTime: 10_000,
    // Creation happens asynchronously and may be retried by the worker.
    refetchInterval: org.autoCreateLinearCleanupIssue ? 30_000 : false,
  });

  // Use detail data while loading, but don't resurrect a deleted link after an
  // authoritative empty response from the history endpoint.
  if (data !== undefined) return data;
  return feature?.linearCleanupIssueUrl
    ? {
        url: feature.linearCleanupIssueUrl,
        details: feature.linearCleanupIssueDetails,
      }
    : null;
}
